import React, { useCallback, useContext, useState } from 'react';

import { MDText } from 'i18n-react';

import { Badge, IconText, InfoIcon } from '@motorway/mw-highway-code';
import { Hyperlink } from '@motorway/mw-highway-code/alpha';
import { Drawer } from '@motorway/mw-highway-code/pre-alpha';

import { VehicleLookupContext } from 'Context/vehicleLookup';

import { EVENT } from 'Utilities/analytics/optimizely/constants';
import { useOptimizelyTrack } from 'Utilities/analytics/optimizely/useOptimizelyTrack';

import type { ServiceFeeTiersResponse } from 'Components/transactionFees/hooks/useServiceFeeData';
import { ServiceFeeExplainer } from 'Components/transactionFees/ServiceFeeExplainer/ServiceFeeExplainer';

import Texts from './ServiceFeeBadge.texts.json';

import styles from './ServiceFeeBadge.scss';

const LocalT = new MDText(Texts);

type ServiceFeeBadgeProps = {
  feeAmountGBP?: number;
  serviceFeeTiers?: ServiceFeeTiersResponse;
};

export const ServiceFeeBadge = ({ feeAmountGBP, serviceFeeTiers }: ServiceFeeBadgeProps) => {
  const { vehicleLookupState: vehicle } = useContext(VehicleLookupContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { OPTIMIZELY_TRACK } = useOptimizelyTrack();

  const dpEnquiryId = vehicle?.enquiry?.dpEnquiryId;

  const openModal = useCallback(() => {
    OPTIMIZELY_TRACK(
      EVENT.SERVICE_FEE_EXPLAINER_MODAL_CLICKED,
      {
        customId: String(dpEnquiryId),
      },
      {
        action: 'confirm_sale',
      },
    );
    setIsModalOpen(true);
  }, [OPTIMIZELY_TRACK, dpEnquiryId]);

  const closeModal = useCallback(() => setIsModalOpen(false), []);

  return (
    <div className={styles.serviceFeesBadge}>
      <Hyperlink
        as='button'
        label={
          <Badge text={
            <IconText
              data-thc-icontext
              reverse
              data-testid="service-fee-applies-notice"
              icon={() => <InfoIcon filled={false} />}
              text={LocalT.translate('badge', { fee: feeAmountGBP })}
            />}
          />
        }
        onClick={openModal}
      />

      {isModalOpen && (
        <Drawer responsive aria-labelledby='service-fee-modal-content' id='service-fee-modal' isOpen={isModalOpen} onClose={closeModal}>
          <ServiceFeeExplainer serviceFeeTiers={serviceFeeTiers ?? undefined} />
        </Drawer>
      )}
    </div>
  );
};
